import React from 'react'
import Head from 'next/head'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import { IS_ASSESSMENT_REVAMP_ENABLED } from 'configs/toggle'
import { useAPIGetHomeBanners } from 'api/home-banners/useAPIHomeBanners'
import BottomNav from 'app/RuangGTKHomepageV3/components/BottomNav'
import Header from 'app/RuangGTKHomepageV3/components/Header'
import Carousel from 'app/RuangGTKHomepageV3/components/Carousel'
import KSCard from 'app/RuangGTKHomepageV3/components/KSCard'
import styles from './styles.module.css'
import MenuPanel from 'app/RuangGTKHomepageV3/components/MenuPanel'
import { useMediaQueryHelper } from 'app/RuangGTKHomepageV3/hooks/useMediaQueryHelper'
import { useCheckShowMenu } from 'app/RuangGTKHomepageV3/hooks/useCheckShowMenu'
import { TRACKER_REFKOM_QUERY_SOURCE_KEY } from 'app/PemetaanKompetensiPage/hooks/useSETrackEventRefkom'
import { useClickTracker } from 'utils/satu-event-tracker'

import type { MenuPanelProps } from 'app/RuangGTKHomepageV3/components/MenuPanel'

const Homepage = () => {
  const {
    showSertifikasiPendidikMenu,
    showPengelolaanKinerjaMenu,
    showRefleksiKompetensiMenu,
  } = useCheckShowMenu()
  const { data } = useAPIGetHomeBanners()
  const { isDesktop } = useMediaQueryHelper()
  const banners = data?.data
  const trackClickVideoInspirasiMenu = useClickTracker({
    activityType: 'CLICK_MENU_VIDEO_INSPIRASI',
    defaultAttributes: {
      screen_name: 'HOMEPAGE',
    },
  })
  const trackClickIdePraktikMenu = useClickTracker({
    activityType: 'CLICK_MENU_IDE_PRAKTIK',
    defaultAttributes: {
      screen_name: 'HOMEPAGE',
    },
  })

  const MENU_PANEL_ITEMS: MenuPanelProps[] = [
    {
      title: 'Belajar Berkelanjutan',
      items: [
        {
          title: 'Diklat',
          href: process.env.NEXT_PUBLIC_LMS_DIKLAT_URL + '?source=rgtk',
          iconUrl: '/icon/ruang-gtk/pr-lms-v1.svg',
          subtitle: 'Pelatihan terbimbing menggunakan LMS.',
        },
        {
          title: 'Sertifikasi Pendidik',
          href: '/sertifikasi-pendidik',
          iconUrl: '/icon/ruang-gtk/pr-sertifikasi-pendidik.svg',
          subtitle: 'Pendidikan Profesi Guru (PPG) bagi Guru tertentu.',
          hide: !showSertifikasiPendidikMenu,
        },
        {
          title: 'Pelatihan Mandiri',
          href: '/pelatihan-mandiri',
          iconUrl: '/icon/ruang-gtk/pr-pelatihan-mandiri-v1.svg',
          subtitle:
            'Ketahui sudah sejauh mana pencapaian belajar murid melalui beragam paket soal.',
        },
        {
          title: 'Komunitas',
          href: '/komunitas?source=HOMEPAGE',
          iconUrl: '/icon/ruang-gtk/pr-komunitas.svg',
          subtitle: '144.000+ komunitas guru & tendik berbagi praktik baik.',
        },
      ],
    },
    {
      title: 'Karir dan Kinerja',
      items: [
        {
          hide: !showPengelolaanKinerjaMenu,
          href: '/pengelolaan-kinerja',
          iconUrl: '/icon/ruang-gtk/pr-pengelolaan-kinerja-v1.svg',
          subtitle:
            'Perencanaan, pelaksanaan, dan penilaian kinerja Anda untuk pengembangan diri dan satdik.',
          title: 'Pengelolaan Kinerja',
        },
        {
          hide: !showRefleksiKompetensiMenu,
          href: `/refleksi-kompetensi?${TRACKER_REFKOM_QUERY_SOURCE_KEY}=pmm_home`,
          iconUrl: '/icon/ruang-gtk/pr-refleksi-kompetensi.svg',
          subtitle: 'Rekomendasi pembelajaran yang disusun personal untuk Anda',
          title: 'Refleksi Kompetensi',
        },
        {
          title: 'Seleksi/Pelatihan BCKS',
          subtitle:
            'Info persyaratan, tahapan, & cek kualifikasi untuk seleksi',
          href: '/bcks/selection',
          iconUrl: '/icon/ruang-gtk/pr-seleksi-kepala-sekolah-v1.svg',
        },
      ],
    },
    {
      title: 'Inspirasi Pembelajaran',
      items: [
        {
          title: 'Perangkat Ajar',
          subtitle:
            'Kegiatan intrakurikuler & kokurikuler, buku teks, dan sejenisnya. Dikurasi Kemendikdasmen.',
          href: '/perangkat-ajar',
          iconUrl: '/icon/ruang-gtk/pr-perangkat-ajar.svg',
        },
        {
          title: 'Video Inspirasi',
          subtitle: 'Kumpulan video terpilih berisi penerapan praktik baik',
          href: '/video-inspirasi',
          iconUrl: '/icon/ruang-gtk/pr-video-inspirasi.svg',
          onClick: () => {
            /** Track click menu video inspirasi */
            trackClickVideoInspirasiMenu(undefined, { keepAlive: true })
          },
        },
        {
          title: 'CP/ATP',
          subtitle:
            'Capaian Pembelajaran dan Alur Tujuan Pembelajaran sesuai mata pelajaran Anda.',
          href: '/kurikulum/referensi-penerapan/capaian-pembelajaran/',
          iconUrl: '/icon/ruang-gtk/pr-cp-atp.svg',
        },
        {
          title: 'Asesmen Murid',
          subtitle:
            'Paket soal dengan pemeriksaan otomatis dan analisis hasil.',
          href: IS_ASSESSMENT_REVAMP_ENABLED
            ? '/assessment'
            : '/assessment/litnum',
          iconUrl: '/icon/ruang-gtk/pr-asesmen-v1.svg',
        },
        {
          title: 'Ide Praktik',
          subtitle:
            'Artikel dan video terpilih tentang praktik pembelajaran terpusat pada murid.',
          href: '/ide-praktik',
          iconUrl: '/icon/ruang-gtk/pr-ide-praktik.svg',
          onClick: () => {
            /** Track click menu ide praktik */
            trackClickIdePraktikMenu()
          },
        },
        {
          title: 'Kelas',
          subtitle:
            'Informasi atau data kelompok murid berdasarkan hasil asesmen',
          href: '/learning-group',
          iconUrl: '/icon/ruang-gtk/pr-kelas.svg',
        },
        {
          title: 'Bukti Karya',
          subtitle: 'Rekam jejak karya, kinerja, dan kompetensi pendidik',
          href: `/bukti-karya?from=HOMEPAGE`,
          iconUrl: '/icon/ruang-gtk/pr-bukti-karya.svg',
        },
      ],
    },
    {
      title: 'Dokumen Rujukan',
      items: [
        {
          title: 'Dokumen Rujukan Pengelolaan Kinerja',
          href: '/dokumen/kategori/Y7G6ArDWqJ',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Pengelolaan Satuan Pendidikan',
          href: '/dokumen/kategori/o450Zr0mMG',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Pembelajaran',
          href: '/dokumen/kategori/oWx0b8DZ1a',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Peningkatan Kompetensi',
          href: '/dokumen/kategori/nxo92w0Eem',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
        },
      ],
    },
    {
      title: 'Lainnya',
      items: [
        {
          title: 'Pusat Bantuan',
          href:
            process.env.NEXT_PUBLIC_PUSAT_INFORMASI_RUMDIK_BASE_URL +
            '/hc/id/categories/44586577246105-Ruang-GTK',
          iconUrl: '/icon/ruang-gtk/pr-pusat-bantuan.svg',
        },
        {
          title: 'Persyaratan Layanan',
          href: '/tns',
          iconUrl: '/icon/ruang-gtk/pr-persyaratan-layanan.svg',
        },
        {
          title: 'Kebijakan Privasi',
          href: '/policy',
          iconUrl: '/icon/ruang-gtk/pr-kebijakan-privasi.svg',
        },
      ],
    },
  ]

  return (
    <>
      <Head>
        <title>Beranda - Ruang GTK</title>
      </Head>
      <div className="relative pb-16 lg:pb-0">
        <Header />
        <div
          className={clsx(
            banners?.length
              ? 'bg-[#2A7EFB] hidden lg:block absolute h-[150px] left-0 right-0 top-[130px]'
              : 'hidden'
          )}
        />
        <section
          className={clsx(
            styles['base-rgtk-homepage'],
            'mx-auto flex w-full lg:!pt-2',
            banners?.length ? 'bg-surface-subdued lg:bg-white' : 'hidden'
          )}
        >
          <Carousel banners={banners} />
        </section>
        <KSCard />
        <section className="bg-gray-0">
          <div
            className={clsx(
              styles['base-rgtk-homepage'],
              'mx-0 flex flex-col w-full',
              'space-y-4 lg:space-y-5'
            )}
          >
            <Text variant={isDesktop ? 'display-sm' : 'heading-md'}>
              Layanan yang Tersedia
            </Text>
            {MENU_PANEL_ITEMS?.map((menuPanel) => (
              <MenuPanel
                key={menuPanel.title}
                title={menuPanel.title}
                items={menuPanel.items}
              />
            ))}
          </div>
        </section>
        <BottomNav />
      </div>
    </>
  )
}

export default Homepage
